import type {
  YachtCategory,
  YachtScorePreview,
  YachtViewerState,
} from "@cloudnest/yacht-engine";
import {
  YACHT_CATEGORIES,
  YACHT_UPPER_BONUS_POINTS,
  YACHT_UPPER_BONUS_THRESHOLD,
} from "@cloudnest/yacht-engine";
import { Fragment } from "react";

export const YACHT_CATEGORY_LABELS: Readonly<Record<YachtCategory, string>> = {
  ONES: "1",
  TWOS: "2",
  THREES: "3",
  FOURS: "4",
  FIVES: "5",
  SIXES: "6",
  CHOICE: "C",
  FOUR_OF_A_KIND: "4K",
  FULL_HOUSE: "FH",
  SMALL_STRAIGHT: "SS",
  LARGE_STRAIGHT: "LS",
  YACHT: "Y",
};

const UPPER_CATEGORY_FACE: Readonly<Partial<Record<YachtCategory, number>>> = {
  ONES: 1,
  TWOS: 2,
  THREES: 3,
  FOURS: 4,
  FIVES: 5,
  SIXES: 6,
};

export type ScoreDieRole = "muted" | "none" | "scoring";

export function getScoreDieRoles({
  category,
  dice,
  score,
  visible,
}: {
  readonly category: YachtCategory;
  readonly dice: readonly (number | null)[];
  readonly score: number | undefined;
  readonly visible: boolean;
}): readonly ScoreDieRole[] {
  if (!visible || score === undefined) return dice.map(() => "none");
  if (score <= 0) return dice.map((die) => (die === null ? "none" : "muted"));

  const upperFace = UPPER_CATEGORY_FACE[category];
  if (upperFace !== undefined) {
    return dice.map((die) => (die === upperFace ? "scoring" : "muted"));
  }

  if (
    category === "CHOICE" ||
    category === "FOUR_OF_A_KIND" ||
    category === "FULL_HOUSE" ||
    category === "SMALL_STRAIGHT" ||
    category === "LARGE_STRAIGHT" ||
    category === "YACHT"
  ) {
    return dice.map((die) => (die === null ? "none" : "scoring"));
  }

  return dice.map(() => "none");
}

export function ScoreDice({
  dice,
  roles,
}: {
  readonly dice: readonly (number | null)[];
  readonly roles: readonly ScoreDieRole[];
}) {
  return (
    <span className="yacht-score-dice">
      {dice.map((die, index) => (
        <span
          className="yacht-score-die"
          data-empty={die === null ? "true" : "false"}
          data-score-role={roles[index] ?? "none"}
          key={index}
        >
          {die ?? "-"}
        </span>
      ))}
    </span>
  );
}

function getPreview(
  previews: readonly YachtScorePreview[],
  category: YachtCategory,
): YachtScorePreview | null {
  return previews.find((preview) => preview.category === category) ?? null;
}

export function ScoreSheetGrid({
  state,
  selectedPlayerId,
  selectedCategory,
  onSelectCategory,
}: {
  readonly state: YachtViewerState;
  readonly selectedPlayerId: string;
  readonly selectedCategory: YachtCategory | null;
  readonly onSelectCategory: (category: YachtCategory) => void;
}) {
  const sheet = state.scoreSheetsByPlayerId[selectedPlayerId];
  const slot = state.turnSlotsByPlayerId[selectedPlayerId] ?? null;
  const viewingSelf = state.viewerPlayerId === selectedPlayerId;
  const previews = viewingSelf ? state.legalActions.scoreableCategories : [];
  const activeDice = slot?.dice ?? [null, null, null, null, null];
  const selectedPlayer = state.players.find(
    (player) => player.playerId === selectedPlayerId,
  );
  const needed = Math.max(
    0,
    YACHT_UPPER_BONUS_THRESHOLD - (sheet?.upperSubtotal ?? 0),
  );

  return (
    <section
      className="yacht-score-panel"
      data-testid="yacht-score-panel"
      data-viewing-self={viewingSelf ? "true" : "false"}
    >
      <header className="yacht-score-header">
        <strong title={selectedPlayer?.displayName ?? selectedPlayerId}>
          {viewingSelf ? "내 점수" : (selectedPlayer?.displayName ?? "상대")}
        </strong>
        <span>{sheet?.total ?? 0}</span>
      </header>
      <table className="yacht-score-table">
        <thead>
          <tr>
            <th>조합</th>
            <th>주사위</th>
            <th>점수</th>
          </tr>
        </thead>
        <tbody>
          {YACHT_CATEGORIES.map((category, index) => {
            const entry = sheet?.entries[category];
            const preview = getPreview(previews, category);
            const selected = selectedCategory === category && preview !== null;
            const rowState =
              entry !== undefined
                ? "confirmed"
                : selected
                  ? "pending"
                  : preview !== null
                    ? "available"
                    : "empty";
            const dice =
              entry?.dice ??
              (selected ? activeDice : [null, null, null, null, null]);
            const score = entry?.score ?? (selected ? preview?.score : null);
            const scoreDieRoles = getScoreDieRoles({
              category,
              dice,
              score: score ?? undefined,
              visible: entry !== undefined || selected,
            });

            return (
              <Fragment key={category}>
                {index === 6 && (
                  <tr className="yacht-bonus-row" key="upper-bonus">
                    <td>보너스</td>
                    <td>
                      {sheet?.upperSubtotal ?? 0}/{YACHT_UPPER_BONUS_THRESHOLD}
                    </td>
                    <td>
                      {sheet?.upperBonus === YACHT_UPPER_BONUS_POINTS
                        ? `+${YACHT_UPPER_BONUS_POINTS}`
                        : `필요 ${needed}`}
                    </td>
                  </tr>
                )}
                <tr
                  className="yacht-score-row"
                  data-recommended={preview?.recommended ? "true" : "false"}
                  data-state={rowState}
                  data-testid={`yacht-score-row-${category.toLowerCase()}`}
                  key={category}
                  onClick={() => {
                    if (preview !== null) onSelectCategory(category);
                  }}
                >
                  <td>
                    <span className="yacht-category-label">
                      {YACHT_CATEGORY_LABELS[category]}
                      {preview?.recommended && (
                        <span
                          aria-hidden="true"
                          className="yacht-recommendation-mark"
                        />
                      )}
                    </span>
                  </td>
                  <td>
                    <ScoreDice dice={dice} roles={scoreDieRoles} />
                  </td>
                  <td className="yacht-score-value">{score ?? "-"}</td>
                </tr>
              </Fragment>
            );
          })}
          <tr className="yacht-total-row">
            <td>총합</td>
            <td>
              Σ {sheet?.upperSubtotal ?? 0} · B {sheet?.upperBonus ?? 0}
            </td>
            <td>{sheet?.total ?? 0}</td>
          </tr>
        </tbody>
      </table>
    </section>
  );
}
